const DAY = 86400000;
const holidayCache = new Map();
const localClock = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Guatemala', year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
});

function dateKey(date) {
  return date.toISOString().slice(0, 10);
}

function addDays(date, count) {
  return new Date(date.getTime() + count * DAY);
}

function easterSunday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const value = h + l - 7 * m + 114;
  return new Date(Date.UTC(year, Math.floor(value / 31) - 1, value % 31 + 1));
}

function nationalHolidays(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const dates = new Set(['01-01', '05-01', '09-15', '10-20', '11-01', '12-25'].map(day => `${year}-${day}`));
  const easter = easterSunday(year);
  [-3, -2, -1].forEach(offset => dates.add(dateKey(addDays(easter, offset))));
  const armyDay = new Date(Date.UTC(year, 5, 30));
  const weekday = armyDay.getUTCDay();
  const offset = weekday === 0 ? 1 : weekday <= 3 ? 1 - weekday : 8 - weekday;
  dates.add(dateKey(addDays(armyDay, offset)));
  holidayCache.set(year, dates);
  return dates;
}

function closedDateSet(value) {
  return new Set(String(value || '').split(/[\s,;]+/).filter(day => /^(?:\d{4}-)?\d{2}-\d{2}$/.test(day)));
}

function isBusinessDay(date, closedDates) {
  const key = dateKey(date);
  const day = date.getUTCDay();
  return day !== 0 && day !== 6 && !nationalHolidays(date.getUTCFullYear()).has(key)
    && !closedDates.has(key) && !closedDates.has(key.slice(5));
}

function nextBusinessDay(date, closedDates) {
  let next = date;
  for (let tries = 0; tries < 370; tries++) {
    next = addDays(next, 1);
    if (isBusinessDay(next, closedDates)) return next;
  }
  throw new Error('No business day available');
}

function estimateDelivery(now = new Date(), closedValue = '') {
  const parts = Object.fromEntries(localClock.formatToParts(now).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  const today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  const closedDates = closedDateSet(closedValue);
  const halfDay = parts.month === 12 && (parts.day === 24 || parts.day === 31);
  const cutoffHour = halfDay ? 12 : 21;
  let confirmation = today;
  if (!isBusinessDay(today, closedDates) || parts.hour >= cutoffHour) {
    confirmation = nextBusinessDay(today, closedDates);
  }
  const preparationStart = nextBusinessDay(confirmation, closedDates);
  const preparationEnd = nextBusinessDay(preparationStart, closedDates);
  const delivery = nextBusinessDay(preparationEnd, closedDates);
  return { today, confirmation, preparationStart, preparationEnd, delivery, cutoffHour };
}

function mountEstimator() {
  if (customElements.get('onlyoni-delivery-estimate')) return;
  class OnlyOniDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.updateEstimate();
      this.timer = window.setInterval(() => this.updateEstimate(), 30000);
      this.onVisible = () => { if (!document.hidden) this.updateEstimate(); };
      document.addEventListener('visibilitychange', this.onVisible);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      document.removeEventListener('visibilitychange', this.onVisible);
    }

    updateEstimate() {
      try {
        const estimate = estimateDelivery(new Date(), this.dataset.closedDates);
        const locale = this.dataset.locale || 'es';
        const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
        const longFormatter = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
        const setDate = (name, date) => {
          const element = this.querySelector(`[data-delivery-${name}]`);
          if (!element) return;
          element.textContent = formatter.format(date);
          element.dateTime = dateKey(date);
          element.title = longFormatter.format(date);
        };
        setDate('order', estimate.confirmation);
        setDate('prep-start', estimate.preparationStart);
        setDate('prep-end', estimate.preparationEnd);
        setDate('arrival', estimate.delivery);
        const sameDay = dateKey(estimate.today) === dateKey(estimate.confirmation);
        const note = this.querySelector('[data-delivery-cutoff]');
        const message = this.dataset[sameDay ? (estimate.cutoffHour === 12 ? 'halfDayMessage' : 'todayMessage') : 'nextMessage'];
        const confirmationHalfDay = estimate.confirmation.getUTCMonth() === 11 && [24, 31].includes(estimate.confirmation.getUTCDate());
        const cutoff = this.dataset[confirmationHalfDay ? 'middayCutoff' : 'eveningCutoff'];
        if (note && message) note.textContent = message.replace('[date]', formatter.format(estimate.confirmation)).replace('[cutoff]', cutoff);
        this.querySelectorAll('[data-delivery-fallback]').forEach(element => { element.hidden = true; });
        this.querySelectorAll('[data-delivery-dates]').forEach(element => { element.hidden = false; });
      } catch (_) {
        this.querySelectorAll('[data-delivery-fallback]').forEach(element => { element.hidden = false; });
        this.querySelectorAll('[data-delivery-dates]').forEach(element => { element.hidden = true; });
      }
    }
  }
  customElements.define('onlyoni-delivery-estimate', OnlyOniDeliveryEstimate);
}

if (typeof document !== 'undefined') mountEstimator();

export { estimateDelivery, nationalHolidays };
